import * as d3 from 'd3';
import each from 'lodash/each';
import find from 'lodash/find';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import reduce from 'lodash/reduce';
import { useCallback, useContext, useEffect, useState } from 'react';

import { IvrEditorContext, IvrEditorLocationStateContext } from 'src/presentation/components/pages/ivr/IvrEditor';
import { DEFAULT_POSITION, NodeSize } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';
import { getElementsMapByDataAid } from 'src/presentation/components/pages/ivr/components/ivrTree/helpers/dragAndDropHelper';
import { getAllNodeLinks } from 'src/presentation/components/pages/ivr/components/ivrTree/helpers/linksHelper';

const useDragNode = ({ nodeId, x, y, onClick }) => {
  const [nodeRef, setNodeRef] = useState<any>();
  const [coordinates, setCoordinates] = useState({ x: 0, y: 0 });
  const { treeData } = useContext(IvrEditorContext);
  const { updateLocations } = useContext(IvrEditorLocationStateContext);

  useEffect(() => {
    setCoordinates({ x, y });
  }, [x, y, setCoordinates]);

  const drag = useCallback(() => {
    let links: any[] = [];
    let dragInProgress = false;
    let sNode: any;

    let domLinks;
    return d3
      .drag()
      .subject(function () {
        return DEFAULT_POSITION;
      })
      .clickDistance(4)
      .on('start', function (event) {
        dragInProgress = false;
        sNode = event.sourceEvent.target.closest('.node');
      })
      .on('drag', event => {
        if (!treeData) {
          return;
        }

        if (!sNode) {
          return;
        }

        if (!dragInProgress) {
          sNode.classList.add('dragging');

          const allLinkPathsElements = getElementsMapByDataAid('path.link');
          const allOptions = getElementsMapByDataAid('.pathOption');

          const nd = treeData.findNodeById(nodeId);
          const links = getAllNodeLinks(treeData.links, nd);

          domLinks = getNodeRelatedLinks({ allLinkPathsElements, allOptions, links });
        }

        dragInProgress = true;

        const nd = treeData.findNodeById(nodeId);
        if (nd) {
          nd.x = coordinates.x + event.x;
          nd.y = coordinates.y + event.y;
          const transform = `translate(${(nd.x = coordinates.x + event.x)}, ${(nd.y = coordinates.y + event.y)})`;
          sNode.setAttribute('transform', transform);
        }

        links = treeData.recalculateLinks(nd);

        each(domLinks, (link: LinkDomElement) => {
          const recalculatedLink = find(
            links,
            recalculatedLink =>
              link.link.source.id === recalculatedLink.source.id &&
              link.link.target.id === recalculatedLink.target.id &&
              link.link.option === recalculatedLink.option &&
              link.link.optionIndex === recalculatedLink.optionIndex
          );

          link.element.setAttribute('d', recalculatedLink.svgLine);
          link.element.setAttribute('stroke-dasharray', '[5, 5]');

          if (link.optionElement) {
            link.optionElement.setAttribute('style', 'display: none');
          }
        });
      })
      .on('end', event => {
        if (!dragInProgress) {
          return;
        }
        if (!sNode) {
          return;
        }

        sNode.classList.remove('dragging');

        each(domLinks, (link: LinkDomElement) => {
          const recalculatedLink = find(
            links,
            recalculatedLink =>
              link.link.source.id === recalculatedLink.source.id &&
              link.link.target.id === recalculatedLink.target.id &&
              link.link.option === recalculatedLink.option &&
              link.link.optionIndex === recalculatedLink.optionIndex
          );

          link.element.setAttribute('d', recalculatedLink.svgLine);
          link.element.setAttribute('stroke-dasharray', '0');

          if (link.optionElement) {
            const points = recalculatedLink.points;

            const point1 = points[points.length - 1];
            const point0 = points[points.length - 2];

            const [x, y] = [
              point0[0] + (point1[0] - point0[0]) / 2 - NodeSize.OptionalNode.width / 2,
              point0[1] + (point1[1] - point0[1]) / 2 - NodeSize.OptionalNode.height / 2,
            ];
            const transform = `translate(${x}, ${y})`;
            link.optionElement.setAttribute('transform', transform);
            link.optionElement.setAttribute('style', 'display: block');
          }
        });

        updateLocations({ [nodeId]: { x: coordinates.x + event.x, y: coordinates.y + event.y } });
      });
  }, [coordinates, nodeId, treeData, updateLocations]);

  useEffect(() => {
    if (nodeRef) {
      const element = d3.select(nodeRef);

      element.call(drag());

      element.on('click', event => {
        if (event.defaultPrevented) {
          return; // click suppressed
        }
        onClick && onClick();
      });
    }
  }, [drag, nodeRef, onClick]);

  return { setNodeRef };
};

type LinkDomElement = { element: HTMLElement; link: any; optionElement: HTMLElement };

function getNodeRelatedLinks({ allLinkPathsElements, allOptions, links }) {
  return reduce(
    links,
    (acc: LinkDomElement[], link) => {
      const linkDataAid = `${link.source.id}-${link.target.id}${
        link.option ? `-${link.option}-${link.optionIndex}` : ''
      }`;
      const element = allLinkPathsElements[linkDataAid];

      if (element) {
        let optionElement;
        if (link.option) {
          const optionDataAid = `ivrTreeNode-${link.source.id}-${
            isNil(link.optionIndex) ? get(link, 'option', '').replace(' ', '-') : link.optionIndex
          }-option`;

          optionElement = allOptions[optionDataAid];
        }

        acc.push({ element, link, optionElement });
      }
      return acc;
    },
    []
  );
}

export default useDragNode;
